import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { apiFetch } from '../../utils/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { User, Company, TenantTheme, Entitlements, AppContextValue } from '../../types';
import { captureLog } from '@/lib/errorReporter';
import { syncAccountingReturnScope } from '@/lib/accountingReturnContext';
import { setDefaultCurrency } from '@/lib/utils';
const __LOG_CTX = { context: 'src/components/hooks/useApp' };

const AppContext = createContext<AppContextValue | null>(null);

// El tema del tenant se persiste en companies.settings.theme (no existe columna
// `theme` nativa en el schema). El resto de la app lee `company.theme`, así que
// lo derivamos aquí, en el único punto donde el objeto company entra al store.
function withTenantTheme(company: Company | null): Company | null {
  if (!company || company.theme) return company;
  const settingsTheme = (company.settings as { theme?: TenantTheme } | null | undefined)?.theme;
  return settingsTheme ? { ...company, theme: settingsTheme } : company;
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  // Entitlements del plan de la empresa activa. `null` = legacy / sin empresa
  // (backend viejo o asesor sin contexto) → el gating cae en comportamiento
  // legacy: nada se oculta ni se bloquea.
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!loading) syncAccountingReturnScope({ userId: user?.id, companyId: company?.id });
  }, [loading, user?.id, company?.id]);

  const contextVersion = useRef(0);
  const activeContext = useRef({ companyId: company?.id, userId: user?.id });
  activeContext.current = { companyId: company?.id, userId: user?.id };
  const loadAppData = useCallback(async (expected?: { companyId: string; userId: string }) => {
    const expectedVersion = contextVersion.current;
    const isCurrent = () => !expected || (expectedVersion === contextVersion.current && activeContext.current.companyId === expected.companyId && activeContext.current.userId === expected.userId);
    if (!isCurrent()) return;
    if (!expected) setLoading(true);
    try {
      const response = await apiFetch<{ user?: User; company?: Company; entitlements?: Entitlements | null }>('/api/auth/me');
      if (!isCurrent() || (expected && (response?.company?.id !== expected.companyId || response?.user?.id !== expected.userId))) return;
      const userData = response?.user || response as unknown as User;
      const companyData = response?.company || null;
      setUser(userData);
      setCompany(withTenantTheme(companyData));
      setEntitlements(response?.entitlements ?? null);
    } catch (_err) {
      if (expected) return; // A failed contextual refresh must preserve the current company.
      if (import.meta.env?.DEV) {
        captureLog('error', __LOG_CTX, 'Error loading app data:', _err);
      }
      setUser(null);
      setCompany(null);
      setEntitlements(null);
    } finally {
      if (!expected) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAppData();
  }, [loadAppData]);

  // ── La empresa activa puede cambiar por debajo ────────────────────────────
  //
  // Hasta ahora la app pedía `/me` UNA sola vez al montar y pintaba ese nombre
  // hasta el siguiente F5. Si la empresa activa de la sesión cambiaba por otra
  // vía —otra pestaña, otro dispositivo, el portal del asesor—, la cabecera
  // seguía rotulando la anterior mientras el backend servía la nueva: el usuario
  // veía una empresa y operaba sobre otra.
  //
  // Y no hay ningún 401 que lo delate: la sesión es coherente consigo misma, así
  // que todo responde 200 con datos del otro tenant. Medido contra dev el
  // 17-09-2026 — la cabecera decía "E2E Smoke SL" (447 facturas) mientras
  // `/auth/me` servía "Smoke 202 Ordinaria SL" (0 facturas), y ARIA contestaba,
  // con razón, que no había ninguna factura.
  //
  // Se revalida cuando el usuario vuelve a la pestaña, que es justo cuando ha
  // podido tocar otra. `focus` y `visibilitychange` se solapan a propósito (no
  // todos los navegadores disparan los dos); el throttle evita el duplicado.
  const lastCheckRef = useRef(0);
  const companyIdRef = useRef<string | null>(null);
  companyIdRef.current = company?.id ?? null;

  const revalidateActiveCompany = useCallback(async () => {
    const known = companyIdRef.current;
    if (!known) return; // sin sesión cargada aún: `loadAppData` ya va por ello
    if (Date.now() - lastCheckRef.current < 30_000) return;
    lastCheckRef.current = Date.now();

    let server: Company | null = null;
    try {
      const res = await apiFetch<{ user?: User; company?: Company; entitlements?: Entitlements | null }>('/api/auth/me');
      server = res?.company ?? null;
      if (!server?.id || server.id === known) return;
      setUser(res?.user ?? null);
      setCompany(withTenantTheme(server));
      setEntitlements(res?.entitlements ?? null);
    } catch {
      // Un fallo de red no es un cambio de empresa: no se toca nada y se
      // reintentará en el próximo foco.
      return;
    }

    // Lo que hay en caché es del tenant anterior. Se purga igual que en un
    // cambio explícito — ver el aviso de `switchCompany` sobre por qué la purga
    // es amplia mientras agentic-erp#1979 siga abierto.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'switchable-companies' });
    toast.info(`Ahora estás en ${server.name}`, {
      description: 'La empresa activa había cambiado en otra sesión. Se han recargado los datos.',
    });
  }, [queryClient]);

  useEffect(() => {
    const onFocus = () => { void revalidateActiveCompany(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') void revalidateActiveCompany(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [revalidateActiveCompany]);

  // Multidivisa (QA sweep) — fija la divisa por defecto de los formateadores a la
  // divisa funcional de la empresa activa. Al cambiar de empresa se reaplica, así
  // que TODOS los importes (Panel, factura, gasto…) se muestran en la divisa
  // correcta sin pasar la divisa en cada call site.
  useEffect(() => {
    setDefaultCurrency(company?.functional_currency);
  }, [company]);

  const switchCompany = useCallback(async (companyId: string, opts?: { hardReset?: boolean }) => {
    contextVersion.current += 1; // Reject contextual refreshes already in flight before this switch.
    // FASE 4 Sub-PR 4.3 — una sola call al endpoint switch-company en BE.
    // El BE actualiza users.company_id + purga Redis user:sub:<sub> + invalida
    // el cache de permisos, todo atomic en una tx + advisory lock.
    const res = await apiFetch<{ user?: User; company?: Company; entitlements?: Entitlements | null }>(
      '/api/auth/switch-company',
      { method: 'POST', body: JSON.stringify({ company_id: companyId }) },
    );
    if (res?.company) setCompany(withTenantTheme(res.company));
    if (res?.user) setUser(res.user);
    // Los entitlements son por-empresa-activa: al cambiar de empresa se
    // reemplazan por los de la respuesta. Si el backend no los incluye
    // (deploy viejo) cae a `null` = gating legacy (no oculta nada) para la
    // nueva empresa, en vez de arrastrar los de la anterior.
    setEntitlements(res?.entitlements ?? null);
    // Por defecto: invalidate por queryKey (no `clear()` nuclear) — los queryKey
    // incluyen company?.id, así que refetchean solos.
    // AD-2a — `hardReset`: al ENTRAR en Modo Dios desde el portal del asesor,
    // purgamos la caché entera (`clear()`) para no arrastrar ni un frame de datos
    // del cliente anterior mientras el caller bloquea el render hasta el switch.
    //
    // D7-04 — desde el fix del cambio de empresa, TODOS los call sites pasan por
    // aquí con `hardReset: true` (`Layout.handleCompanyChange`, `PortalAsesor`,
    // `AdvisorWorkspace`). La rama `invalidateQueries()` se queda para el resto
    // de usos y para no cambiar el contrato del hook.
    //
    // ⚠️ El `clear()` NO es la solución definitiva: está tapando que 10 claves
    // `['asesor', …]` no llevan `company?.id`, contra la regla de oro del repo.
    // El fix de raíz es agentic-erp#1979 (migrarlas al helper `qk.*`). **No
    // relajar este `clear()` a purga selectiva antes de que #1979 esté dentro**:
    // sin `companyId` en la clave, la del cliente nuevo COLISIONA con la del
    // anterior y la fuga vuelve, en sesión de asesor y con datos fiscales.
    if (opts?.hardReset) {
      // `switchable-companies` (`useUserCompaniesCount`) es la ÚNICA excepción
      // segura: está keyed solo por `userId` (no por company), así que no es
      // sensible al leak cross-tenant que bloquea relajar este `clear()` para
      // el resto de claves (aviso arriba). Perderla en el `clear()` hace que
      // `useUserCompaniesCount()` devuelva `0` durante el refetch — y
      // `FirmRouteGate`/`FirmHomeGate` (que gatean "Mis empresas" con ese
      // count) rebotan la navegación de vuelta al portal a mitad del
      // `navigate('/home')` de `PortalAsesor.handleEnter`: el clic en "Entrar"
      // nunca aterriza en la empresa.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'switchable-companies' });
    } else {
      queryClient.invalidateQueries();
    }
  }, [queryClient]);

  const value: AppContextValue = {
    user,
    company,
    currentCompany: company,
    loading,
    isLoading: loading,
    refresh: loadAppData,
    switchCompany,
    entitlements,
    // Setter directo para actualizar los entitlements sin re-pedir /me tras un
    // cambio de plan (PATCH /api/onboarding/plan devuelve los nuevos). Cae de
    // vuelta a `refresh()` si el caller no tiene el payload.
    setEntitlements,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    return {
      user: null,
      company: null,
      currentCompany: null,
      loading: true,
      isLoading: true,
      refresh: async () => {},
      switchCompany: async () => {},
      entitlements: null,
      setEntitlements: () => {},
    };
  }
  return context;
}

export function useCurrentCompany(): { data: Company | null; isLoading: boolean; error: null } {
  const { company, loading } = useApp();
  return { data: company, isLoading: loading, error: null };
}

/**
 * Selector conveniente para los entitlements del plan de la empresa activa.
 * Devuelve `null` cuando no hay empresa activa o el backend no los envía
 * (deploy viejo) — en ese caso el consumidor debe tratar el gating como
 * legacy (no ocultar ni bloquear). Espeja el patrón de `useCurrentCompany`.
 */
export function useEntitlements(): Entitlements | null {
  const { entitlements } = useApp();
  return entitlements;
}

export default useApp;
